import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import {
    Cloud,
    CloudDrizzleIcon,
    CloudRain,
    CloudSun,
    Droplet,
    Info,
    MapPin,
    Sun,
    Wind
} from 'lucide-react'

import { useDispatch, useSelector } from 'react-redux'
import { getWeatherData } from '../../../../app/modules/core/_request'
import { useSnackbar } from '../../../context/SnackbarContext'
import { useLocation } from '../../../../hooks/useLocation'
import { IHours, LocationState, WeatherAndLocationData } from '../../../../app/PublicPages/core/models'
import { envConfig } from '../../../../utils/config'
import { AppDispatch, RootState } from '../../../../redux/store'
import { setWeatherDataAction } from '../../../../redux/actions/weather.action'
import SimpleLoader from '../../../../app/modules/apps/components/SimpleLoader'
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

const WeatherInfo: FC = () => {
    const { showSnackbar } = useSnackbar()
    const [isLoading, setIsLoading] = React.useState(false)
    const { latitude, longitude, loading }: LocationState = useLocation()
    const abortRef = useRef<AbortController | null>(null);

    const dispatch = useDispatch<AppDispatch>();
    const { weatherData, isWeatherDataLoaded }: { weatherData: WeatherAndLocationData, isWeatherDataLoaded: boolean } =
        useSelector((state: RootState) => state.weatherReducer);

    const { lat, lng } = envConfig.APP_WEATHER_DEFAULT_COORDINATES

    // -----------------------------
    // ICON MAPPER (stable function)
    // -----------------------------
    const getWeatherIcon = useCallback((icon?: string, size = 20) => {
        switch (icon) {
            case 'clear-day':
                return <Sun className="text-white" size={size} />
            case 'partly-cloudy-day':
                return <CloudSun className="text-white" size={size} />
            case 'cloudy':
                return <Cloud className="text-white" size={size} />
            case 'rain':
                return <CloudRain className="text-white" size={size} />
            default:
                return <Cloud className="text-white" size={size} />
        }
    }, [])

    // -----------------------------
    // API CALL (stable)
    // -----------------------------
    const handleGetWeatherData = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);
        try {
            const response = await getWeatherData({
                lat: latitude || lat,
                lon: longitude || lng,
            }, controller.signal);

            if (controller.signal.aborted) return;

            if (response.status === "ok" && response.data) {
                dispatch(
                    setWeatherDataAction({
                        data: response.data,
                    }),
                );
            } else {
                showSnackbar(response.message, "danger");
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error(error);
            showSnackbar(getApiErrorMessage(error, "Something went wrong"), "danger");
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [dispatch, lat, latitude, lng, longitude, showSnackbar]);

    // -----------------------------
    // MEMO: hours extraction
    // -----------------------------
    const hours = useMemo<IHours[]>(() => {
        return weatherData?.weather?.days?.[0]?.hours ?? []
    }, [weatherData])

    // -----------------------------
    // MEMO: current + next 5 hours
    // -----------------------------
    const next5Hours = useMemo(() => {
        const currentHour = new Date().getHours()
        return hours.slice(currentHour, currentHour + 5)
    }, [hours])

    // -----------------------------
    // MEMO: day formatter (IMPORTANT)
    // -----------------------------
    const dayNameFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat('en-US', {
                weekday: 'long',
            }),
        []
    )

    // -----------------------------
    // FETCH ONLY WHEN READY
    // -----------------------------
    useEffect(() => {
        if (!loading && !isWeatherDataLoaded) {
            handleGetWeatherData();
        }
        return () => {
            abortRef.current?.abort();
        };
    }, [loading, handleGetWeatherData, isWeatherDataLoaded])

    // -----------------------------
    // UI
    // -----------------------------

    return (
        <div
            className="widget-header-menu flex-column w-100 p-3 rounded-4 w-md-400px w-fit-content"
        >         <div className="menu-item rounded-4 w-100 p-0" >
                {isLoading && <SimpleLoader />}
                <div className="menu-content px-0 rounded-4 pb-0 gap-5" >
                    {(!latitude || !longitude ) && loading==false && <div className="px-3 mb-4">
                        <div className="w-100 p-2 bg-light-warning border border-warning rounded-4 d-flex align-items-start gap-2 text-warning">
                            <Info className="text-warning" size={24} /> <p className="mb-0 fs-8">Location permission was not provided, so the default weather for Western Cape is being displayed.</p>
                        </div>
                    </div>}
                    {/* LOCATION */}
                    <div className="w-100 px-3 d-flex justify-content-between">
                        <p className="text-white fs-5 d-flex gap-2">
                            <MapPin size={20} />
                            <span>{weatherData?.location}</span>
                        </p>

                        <p className="text-white fs-6 text-end">
                            {weatherData?.weather?.currentConditions?.conditions}
                        </p>
                    </div>

                    {/* TEMP */}
                    <div className="w-100 px-3 d-flex justify-content-between">
                        <p className="text-white fw-bold fs-1">
                            {weatherData?.weather?.currentConditions?.temp}°C
                        </p>

                        {getWeatherIcon(
                            weatherData?.weather?.currentConditions?.icon,
                            40
                        )}
                    </div>

                    {/* STATS */}
                    <div className="w-100 d-flex justify-content-around mt-5">
                        <div className="d-flex p-3 gap-3 bg-primary bg-opacity-20 rounded-3">
                            <CloudDrizzleIcon className="text-white" size={20} />
                            <p className="text-white fs-5 mb-0">
                                {weatherData?.weather?.currentConditions?.precipprob}%
                            </p>
                        </div>

                        <div className="d-flex p-3 gap-3 bg-primary bg-opacity-20 rounded-3">
                            <Droplet className="text-white" size={20} />
                            <p className="text-white fs-5 mb-0">
                                {weatherData?.weather?.currentConditions?.humidity}%
                            </p>
                        </div>

                        <div className="d-flex p-3 gap-3 bg-primary bg-opacity-20 rounded-3">
                            <Wind className="text-white" size={20} />
                            <p className="text-white fs-5 mb-0">
                                {weatherData?.weather?.currentConditions?.windspeed}km/h
                            </p>
                        </div>
                    </div>

                    {/* HOURS */}
                    <div className="w-100 d-flex px-0 justify-content-between mt-5">
                        {next5Hours.map((item, index) => (
                            <div
                                key={item.datetime}
                                className="d-flex p-3 pb-0 align-items-center flex-column gap-3 rounded-3"
                            >
                                <p className="text-white fs-5 mb-0">
                                    {item.datetime?.slice(0, 5)}
                                </p>

                                {getWeatherIcon(item.icon, 20)}

                                <p className="text-white fs-5 mb-0">
                                    {Math.round(item.temp)}°
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* DAYS */}
                    <div className="w-100 d-flex flex-column mt-5 gap-2 bg-white bg-opacity-10 rounded-bottom-4" >
                        {weatherData?.weather?.days?.slice(0, 5)?.map((day, index) => (
                            <div
                                key={day.datetime}
                                className="d-flex align-items-center px-3 justify-content-between py-2 rounded-4"
                            >
                                <p className="text-white fs-5 flex-grow-1 m-0">
                                    {dayNameFormatter.format(new Date(day.datetime))}
                                </p>

                                <div className="d-flex justify-content-center w-100px">
                                    {getWeatherIcon(day.icon, 20)}
                                </div>

                                <p className="text-white fs-5 text-end m-0 w-100px">
                                    {Math.round(day.tempmax)}° {Math.round(day.tempmin)}°
                                </p>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </div>
    )
}

export { WeatherInfo }